import { Fragment } from 'react';
import { TableCell, TableRow, Tag, TextBody } from '@pypestream/design-system';
import { AddProjectCell } from './add-project-cell';
import { ProjectProductRoleCell } from './project-product-role-cell';
import { ProjectProductCell } from './project-product-cell';
import { ProjectNameCell } from './project-name-cell';
import { OrgCell } from './org-cell';
import './permissions-table.scss';
import {
  HandleAddProject,
  HandleAddTeam,
  HandleOrgCollapse,
  HandleRemoveProjectWithRoles,
  HandleRemoveTeam,
  HandleUpdateManagerRole,
  HandleUpdateProjectProductRole,
  ShortOrgType,
} from './permissions-table.types';

export type TableRowContentProps = {
  org: ShortOrgType;
  canManageAccess: boolean;
  isAddingTeamToOrg: boolean;
  isSavingDetails: boolean;
  handleOrgCollapse: HandleOrgCollapse;
  handleUpdateManagerRole: HandleUpdateManagerRole;
  handleRemoveTeam?: HandleRemoveTeam;
  handleAddTeam?: HandleAddTeam;
  handleUpdateProjectProductRole: HandleUpdateProjectProductRole;
  handleAddProject: HandleAddProject;
  handleRemoveProjectWithRoles: HandleRemoveProjectWithRoles;
};

export const TableRowContent = ({
  org,
  canManageAccess,
  isAddingTeamToOrg,
  isSavingDetails,
  handleOrgCollapse,
  handleUpdateManagerRole,
  handleRemoveTeam,
  handleAddTeam,
  handleUpdateProjectProductRole,
  handleAddProject,
  handleRemoveProjectWithRoles,
}: TableRowContentProps) => {
  return org.isCollapsed ? (
    <Fragment>
      <TableRow
        className="c-permissions-table--org-row c-permissions-table--org-row--collapsed"
        onClick={() => {
          handleOrgCollapse({
            orgId: org.id || '',
            collapse: !org.isCollapsed,
          });
        }}
      >
        <OrgCell
          org={org}
          isAddingTeamToOrg={isAddingTeamToOrg}
          canManageAccess={canManageAccess}
          isSavingDetails={isSavingDetails}
          handleUpdateManagerRole={handleUpdateManagerRole}
          handleOrgCollapse={handleOrgCollapse}
          handleRemoveTeam={handleRemoveTeam}
          handleAddTeam={handleAddTeam}
        />
        <TableCell colspan={3}>
          {org.assignedProjects?.map((prj) => (
            <Tag
              className="tag-spaced"
              minimal
              variant="indicator"
              newVariant="filled"
              key={prj.id}
            >
              {prj.project.name}
            </Tag>
          ))}
          {!org.assignedProjects?.length && (
            <TextBody
              variant="secondary"
              size="small"
              i18nKey="manager/common:permissionAndAccessTable.noProjectsAssigned"
            >
              No projects assigned
            </TextBody>
          )}
        </TableCell>
      </TableRow>
    </Fragment>
  ) : (
    <Fragment>
      <TableRow className="c-permissions-table--org-row">
        <OrgCell
          org={org}
          isAddingTeamToOrg={isAddingTeamToOrg}
          canManageAccess={canManageAccess}
          isSavingDetails={isSavingDetails}
          handleUpdateManagerRole={handleUpdateManagerRole}
          handleOrgCollapse={handleOrgCollapse}
          handleRemoveTeam={handleRemoveTeam}
          handleAddTeam={handleAddTeam}
        />
        {(org.assignedProjects && org.assignedProjects.length > 0 && (
          <>
            <ProjectNameCell
              project={org.assignedProjects[0]}
              handleRemoveProjectWithRoles={handleRemoveProjectWithRoles}
              canManageAccess={canManageAccess}
            />
            <ProjectProductCell
              projectProductSetting={
                org.assignedProjects[0].project.project_product_settings[0]
              }
            />
            <ProjectProductRoleCell
              project={org.assignedProjects[0]}
              productId={
                org.assignedProjects[0].project.project_product_settings[0]
                  .product.id
              }
              key={`${org.assignedProjects[0].project.id}-${org.assignedProjects[0]._account_project_roles.length}`}
              canManageAccess={canManageAccess}
              isSavingDetails={isSavingDetails}
              handleUpdateProjectProductRole={handleUpdateProjectProductRole}
            />
          </>
        )) ||
          (org.showAddProjectButton && (
            <AddProjectCell
              org={org}
              isSavingDetails={isSavingDetails}
              handleAddProject={handleAddProject}
            />
          )) || (
            // Else render empty cells to keep table alignment as expected
            <>
              <TableCell key="empty-cell-1" colspan={3}>
                <TextBody
                  variant="secondary"
                  size="small"
                  i18nKey="manager/common:permissionAndAccessTable.projectsEmptyStateForViewUser"
                >
                  This Org has no Projects. Contact an admin to get started.
                </TextBody>
              </TableCell>
            </>
          )}
      </TableRow>
      {org.assignedProjects?.map((project, projectIndex) => {
        if (projectIndex === 0) {
          return project.project?.project_product_settings
            ?.slice(1)
            ?.map((projectProductSetting) => {
              return (
                <TableRow
                  key={`${project.project.id}-${projectProductSetting.product?.id}`}
                >
                  <ProjectProductCell
                    projectProductSetting={projectProductSetting}
                  />
                  <ProjectProductRoleCell
                    project={project}
                    productId={projectProductSetting.product.id}
                    key={`${project.project.id}-${project._account_project_roles.length}`}
                    canManageAccess={canManageAccess}
                    isSavingDetails={isSavingDetails}
                    handleUpdateProjectProductRole={
                      handleUpdateProjectProductRole
                    }
                  />
                </TableRow>
              );
            });
        }
        return (
          <Fragment key={project.project.id}>
            <TableRow>
              <ProjectNameCell
                project={project}
                handleRemoveProjectWithRoles={handleRemoveProjectWithRoles}
                canManageAccess={canManageAccess}
              />
              {project.project?.project_product_settings?.length > 0 ? (
                <>
                  <ProjectProductCell
                    projectProductSetting={
                      project.project?.project_product_settings[0]
                    }
                  />
                  <ProjectProductRoleCell
                    project={project}
                    productId={
                      project.project?.project_product_settings[0].product.id
                    }
                    key={`${project.project.id}-${project._account_project_roles.length}`}
                    canManageAccess={canManageAccess}
                    isSavingDetails={isSavingDetails}
                    handleUpdateProjectProductRole={
                      handleUpdateProjectProductRole
                    }
                  />
                </>
              ) : (
                // Else render empty cells to keep table alignment as expected
                <>
                  <TableCell key="empty-cell-4"></TableCell>
                  <TableCell key="empty-cell-5"></TableCell>
                </>
              )}
            </TableRow>
            {project.project?.project_product_settings?.map(
              (projectProductSetting, projectProductSettingInd) => {
                if (projectProductSettingInd === 0) {
                  return null;
                }
                return (
                  <TableRow
                    key={`${project.project.id}-${projectProductSetting.product?.id}`}
                  >
                    <ProjectProductCell
                      projectProductSetting={projectProductSetting}
                    />
                    <ProjectProductRoleCell
                      project={project}
                      productId={projectProductSetting.product.id}
                      key={`${project.project.id}-${project._account_project_roles.length}`}
                      canManageAccess={canManageAccess}
                      isSavingDetails={isSavingDetails}
                      handleUpdateProjectProductRole={
                        handleUpdateProjectProductRole
                      }
                    />
                  </TableRow>
                );
              }
            )}
          </Fragment>
        );
      })}
      {((org.assignedProjects || []).length > 0 &&
        org.showAddProjectButton &&
        (org.availableProjects || []).length > 0 && (
          <TableRow>
            <AddProjectCell
              org={org}
              isSavingDetails={isSavingDetails}
              handleAddProject={handleAddProject}
            />
          </TableRow>
        )) ||
        null}
    </Fragment>
  );
};
